import { Package } from "lucide-react";

import type { Order, OrderStatusEnum } from "@/types/order";

import { cn, genOrderProgressVal } from "@/lib/utils";
import useAuthStore from "@/store/auth";
import { Link } from "@tanstack/react-router";

import { StatusTag } from "../order/status-tag";
import { Progress } from "../ui/progress";
import { Skeleton } from "../ui/skeleton";
import { TabsContent } from "../ui/tabs";

interface OrderTabProps {
	value: string;
	isLoading?: boolean;
	orders: Order[];
	content: {
		title: string;
		label?: string;
	};
}

export function OrderTab({ value, orders, content, isLoading }: OrderTabProps) {
	const user = useAuthStore(state => state.user);

	if (!user)
		return null;

	return (
		<TabsContent
			value={value}
			className="min-h-[80vh] p-3 bg-white rounded-xl"
		>
			<div className={cn("flex flex-col min-h-[80vh] gap-4 flex-wrap h-full", {
				"items-center justify-center ": !orders.length && !isLoading
			})}
			>
				{
					isLoading
						? (
								<>
									{Array.from({ length: 5 }).map(() => (
										<Skeleton key={crypto.randomUUID()} className="h-14 w-full" />
									))}
								</>
							)
						: orders.length
							? orders.map(order => (

									<Link
										key={order.id}
										to={user.userType === "BUYER" ? "/buyer/orders/$orderid" : "/vendor/orders/$orderid"}
										params={{ orderid: order.id }}
									>
										<div className="border rounded-2xl p-4 shadow-sm">
											<div className="flex flex-wrap justify-between items-start mb-4">
												<div>
													<h3 className="font-bold mb-1">{order.contract?.title}</h3>
													<p className="text-gray-500 text-sm">
														{`Contract code: ${order.contract.contractCode}`}
													</p>
												</div>
												<StatusTag status={order.status.toUpperCase() as OrderStatusEnum} />
											</div>
											<Progress value={genOrderProgressVal(order.dispatchStatus)} className="my-2" />
											<div className="flex justify-between items-center">
												<div className="space-y-4">
													<div className="flex items-center gap-3">
														<Package className="text-gray-400" size={20} />
														<div>
															<p className="text-xs text-gray-500">Transit</p>
															<p className="font-medium text-xs">{order.dropoffLocation?.address ?? "Unavailable"}</p>
														</div>
													</div>
													<div className="flex items-center gap-3">
														<Package className="text-gray-400" size={20} />
														<div>
															<p className="text-xs text-gray-500">Pickup Location</p>
															<p className="font-medium text-xs">{order.pickupLocation?.address}</p>
														</div>
													</div>
												</div>

												<div className="flex items-center gap-4">
													<Package className="text-primary" size={48} />
												</div>
											</div>
										</div>
									</Link>
								))
							: (
									<div className="space-y-4 *:text-center">
										<div className="m-auto flex items-center justify-center bg-hover-secondary size-52 rounded-full">
											<img src="/3d-approved.png" alt="order icon" />
										</div>
										<div>
											<h3 className="text-lg font-bold">{content?.title}</h3>
											{content.label && <p className="text-muted-foreground">{content.label}</p>}
										</div>
									</div>
								)
				}
			</div>
		</TabsContent>
	);
}
